import type { InfiniteData } from "@tanstack/react-query";

import type { Mountaya, PagedResponse } from "@mountaya/client";
import { MountayaConfigurationError } from "@mountaya/client";

import type { OperationArgsOf } from "./mountayaKeys.js";
import {
  isScopeSettled,
  mountayaInfiniteQueryOptions,
  type OperationInfiniteQueryOptions,
  type OperationQueryOptions,
  selectData,
  taggedKey,
} from "./operationQueryOptions.js";
import { studioResourceKey } from "./studioResourceKey.js";

/**
 * The reads a map studio is built on (itineraries, collections and zones), each as the options
 * TanStack Query takes for it.
 *
 * Each read is defined ONCE, as a `...QueryOptions` builder over a client, and bound twice: as a
 * hook for a component (`hooks/studioResources.ts`), and directly for a caller outside React. A
 * route loader or a Server Component prefetching an itinerary and the studio that renders it have
 * to land on the same cache entry. If they did not, the prefetch would fetch, the component would
 * fetch again, and the surface would flash empty on a navigation that had already paid for the
 * data. Nothing here imports React, which is what lets `@mountaya/client-react/query` publish
 * these builders to a server.
 *
 * **A read writes under the key the cache invalidates.** A resource read resolves its key
 * through `studioResourceKey`, which is what `StudioCache` invalidates through, and a listing's
 * key extends the one `studioResourceKey` builds for its ref. That is the invariant the write
 * path depends on: a studio tool persisting a segment invalidates `{ kind: "itinerary", id }`,
 * and were that a different key from the one `useItinerary` wrote under, the editor would keep
 * rendering a version the server has replaced, with no error at any layer to say so.
 *
 * **An entry holds the operation's answer as the client returns it.** Each fetcher answers the
 * whole body and each builder carries a `select` that projects the record out of it, so a read
 * here and `mountayaQueryOptions` of the same operation share one entry, each answering its own
 * shape. A listing is read a page at a time, and its entry holds the pages, each a body: it is
 * the entry `mountayaInfiniteQueryOptions` writes for the same arguments, and its `select` joins
 * the rows of every page.
 *
 * **Nothing here validates a response.** The client already types every body from the contract
 * and decodes the instants it declares, so a second narrowing in this package would be the
 * contract restated by hand, one that goes stale silently because nothing compares it back to
 * the schema it was copied from. Where a caller needs a narrower shape (a sidebar row, a picker
 * option), that is a projection at the read edge via `select`, not a parse.
 *
 * **An id gate is folded into each read, not left to the caller.** Every read below but the
 * listings addresses one resource, and every surface that mounts one renders before the id
 * exists: a blank studio, a closed picker, a create flow. `undefined` is how that state is
 * spelled, and the read stays disabled. The KEY still has to be built for a disabled read, so an
 * absent id becomes `""` there and only there; the fetcher refuses it outright, because
 * `enabled` is not a gate a render-free `ensureQueryData` respects. An empty string handed in as
 * an id is read the same way, as no id: it is the value that key holds, and the path it would
 * build is the listing's. Each fetcher is `async`, so the refusal is a rejection, as a request's
 * failure is, for a caller that runs it by hand as for TanStack.
 *
 * **A read waits for its credential scope to settle.** Every key here names the client's
 * credential scope, and a session token source's first token refines that scope with the user it
 * names. So `enabled` is also false while `transport.credentialScopePending`: a read held until
 * the token lands is keyed once, under the scope it settles, where one run before it would be
 * read again under that scope. The hold costs nothing, as a request made then waits for the same
 * token. `ensureQueryData` and `prefetchQuery` run the fetcher whatever `enabled` says.
 */

/**
 * StudioQueryOptions is one studio read: the entry it caches under, how to fetch it, whether it
 * has anything to address yet, how long its answer stays fresh, and the record it projects out
 * of the body the entry holds.
 *
 * The freshness policy sits with the read because TanStack's default is a policy as well:
 * `staleTime: 0` refetches every one of these on every mount, and a studio mounts on every
 * navigation into the editor. Leaving `staleTime` to each surface would cost that refetch on
 * every surface that does not know it has to choose, so the value is set here, where its reason
 * lives: an entity is refetched by the invalidation a write triggers, and a listing is what the
 * reader themself edits. A hook spreads the caller's own options LAST, so the freshness is a
 * default rather than a policy taken away. `enabled` is the one member a caller joins and never
 * replaces: a hook reads only while this gate and the caller's are both open.
 */
export interface StudioQueryOptions<TBody extends { readonly data: unknown }, TData = TBody["data"]>
  extends OperationQueryOptions<TBody> {
  /**
   * Whether the read may run now: false while its id is `undefined` or empty, and while the
   * client's credential scope is still to be told who the session acts as, so a hook bound to it
   * stays idle whatever `enabled` its caller passes. `ensureQueryData` and `prefetchQuery` run the
   * fetcher regardless, and the fetcher refuses an absent id for them.
   */
  readonly enabled: boolean;
  /** Projects the record out of the body the entry holds. */
  readonly select: (body: TBody) => TData;
  /**
   * How long this read is served without revalidating, in milliseconds.
   *
   * Stated per read rather than left at TanStack's `0`, which refetches on every mount, and a
   * studio mounts on every navigation into the editor. Each window states what CHANGES the
   * answer: thirty seconds for a list the reader themself edits, and five minutes for one
   * resource and for the tile specification derived from it.
   */
  readonly staleTime: number;
}

/** The body one operation of the client's namespaces resolves to. */
type BodyOf<TOperation extends (...args: never[]) => Promise<{ readonly data: unknown }>> = Awaited<
  ReturnType<TOperation>
>;

type Itineraries = Mountaya["studio"]["itineraries"];
type Collections = Mountaya["studio"]["collections"];
type Zones = Mountaya["studio"]["zones"];
type Tiles = Mountaya["tiles"];

/**
 * LISTING_STALE_MS is how long a listing is served without revalidating.
 *
 * Short, because a listing is exactly what the reader changes: they create an itinerary, rename
 * one, move one between collections, and then look at the list. Thirty seconds is long enough
 * that switching scope back and forth is free and short enough that a change made in another
 * tab shows up without a reload.
 */
const LISTING_STALE_MS = 30 * 1000;

/**
 * ENTITY_STALE_MS is how long one resource, and the tile specification derived from it, is
 * served without revalidating.
 *
 * Longer than a listing, because an entity read is the thing the reader is looking AT: it is
 * refetched by the invalidation a write triggers, which is exact, so a time-based refetch only
 * catches somebody else's edit. Five minutes is the interval at which that is worth a request.
 */
const ENTITY_STALE_MS = 5 * 60 * 1000;

/** The credential scope every key below is built under. */
const scopeOf = (client: Mountaya): string => client.transport.credentialScope;

/**
 * shared states the members every read carries: TanStack's retries off, since the client already
 * retries, and the record projected out of the body.
 */
const shared = { retry: false, select: selectData } as const;

/**
 * isNamed answers whether a caller named a resource: an id that is neither `undefined` nor empty.
 *
 * The empty string is no id. It is what a builder writes into the key of a read with nothing to
 * address, and the path it builds, `GET /v1/itineraries/`, is the listing's.
 */
const isNamed = (id: string | undefined): id is string => id !== undefined && id !== "";

/**
 * requireId refuses to build a request for a resource the caller has not named.
 *
 * `enabled: false` is the whole gate for a HOOK: TanStack does not call the fetcher. It is no
 * gate at all for `ensureQueryData` or `prefetchQuery`, which run the fetcher regardless, so a
 * loader prefetching an absent id would otherwise send `GET /v1/itineraries/` and read the 404
 * or, worse, the collection listing behind it. Throwing here names the builder and the id it
 * lacks, and the caller gets a stack pointing at the loader rather than a puzzling response.
 *
 * @param id The id the builder was handed.
 * @param builder The builder's name, which opens the message.
 * @param resource What the id names, such as `itinerary` or `user`.
 * @returns The id, once it names something.
 * @throws MountayaConfigurationError For an id that is `undefined` or empty.
 */
const requireId = (id: string | undefined, builder: string, resource: string): string => {
  if (!isNamed(id)) {
    // The family's own class rather than a bare `Error`, so a consumer's
    // `catch (e) { if (e instanceof MountayaError) }` covers a misuse of these helpers the same
    // way it covers one of the client's. A configuration error is the right member: nothing
    // reached the network, and no retry would help.
    throw new MountayaConfigurationError(
      `${builder}: the ${resource} id is ${id === undefined ? "undefined" : "an empty string"}, ` +
        "so there is nothing to read. Resolve the id before running the fetcher: a hook stays " +
        "idle until it exists, and ensureQueryData and prefetchQuery run the fetcher whatever " +
        "`enabled` says.",
    );
  }

  return id;
};

/**
 * StudioInfiniteQueryOptions is one studio listing, read a page at a time: the walk
 * {@link OperationInfiniteQueryOptions} describes, how long its pages stay fresh, and the rows of
 * every page read so far, in the listing's order.
 *
 * A listing has nothing to address, so it is enabled as soon as the credential scope has settled;
 * a caller holding a filter that is not resolved yet (an id still loading) gates the read with
 * its own `enabled`, since a filter left `undefined` is a filter not sent, and the listing it
 * reads is the wider one.
 */
export interface StudioInfiniteQueryOptions<TPage extends PagedResponse<unknown>>
  extends OperationInfiniteQueryOptions<TPage> {
  /** Joins the rows of every page read so far, in the listing's order. */
  readonly select: (pages: InfiniteData<TPage, string | null>) => TPage["data"];
  /**
   * How long the listing is served without revalidating, in milliseconds: thirty seconds, as
   * for every listing the reader themself edits.
   */
  readonly staleTime: number;
}

/**
 * QueryOf is the query one listing takes: its filters, its `sort`, its `limit` and the `cursor`
 * a walk starts at.
 */
type QueryOf<Op extends "getCollections" | "getItineraries" | "getZones"> = NonNullable<
  OperationArgsOf<"rest", Op>["query"]
>;

/**
 * SpecificationQueryOf is the query a collection's specification is read with: the layers it
 * declares.
 */
type SpecificationQueryOf<Op extends "getCollectionSpecification"> = NonNullable<
  OperationArgsOf<"tiles", Op>["query"]
>;

/**
 * selectRows joins the rows of every page a walk has read.
 *
 * One function for every listing, so TanStack, which re-runs a `select` whose identity changed,
 * runs it once per answer.
 */
const selectRows = <TPage extends PagedResponse<unknown>>(
  pages: InfiniteData<TPage, string | null>,
): TPage["data"] => pages.pages.flatMap((page) => page.data);

/**
 * listing binds one listing's walk to the studio's freshness and projection, reading each page
 * through the client's own namespace as every studio read does.
 *
 * The walk's key extends the operation's own for the same arguments, which is the key
 * `studioResourceKey` builds for the listing's ref narrowed by the same filters: invalidating
 * `{ kind: "itineraries" }`, or a ref naming a subset of the filters a listing was read with,
 * reaches the walk through TanStack's prefix matching.
 *
 * @param walk The walk {@link mountayaInfiniteQueryOptions} describes for the same arguments,
 *   whose key and cursors the listing keeps.
 * @param page Reads the page that starts at `cursor`, the first one where it is `undefined`.
 */
const listing = <TPage extends PagedResponse<unknown>>(
  walk: OperationInfiniteQueryOptions<TPage>,
  page: (cursor: string | undefined, signal: AbortSignal | undefined) => Promise<TPage>,
): StudioInfiniteQueryOptions<TPage> => ({
  ...walk,
  queryFn: async ({ pageParam, signal }) => page(pageParam ?? undefined, signal),
  select: selectRows,
  staleTime: LISTING_STALE_MS,
});

/**
 * itinerariesInfiniteQueryOptions reads the itineraries the client's credential reaches, a page
 * at a time, filtered and sorted by `query`: the list a studio's sidebar renders.
 *
 * The listing is always the organization of the client's key: `organization_id` may name it,
 * which changes nothing, and naming another is refused with a 403. The session decides which
 * itineraries appear. The key alone, or with a session token naming nobody, lists the public
 * itineraries that are currently published, each as last published, and none while the
 * organization's agreement does not include Map Publishing; with a session token naming a member,
 * the private and unpublished ones too, each as drafted. A personal itinerary, owned by no
 * organization, never appears.
 *
 * @param client The client that reads the listing, and whose credential scopes the entry.
 * @param query The listing's filters, its `sort` and its page size. Omitted, every itinerary
 *   the credential reaches, most recently changed first.
 * @returns The options `useInfiniteQuery`, `prefetchInfiniteQuery` and `fetchInfiniteQuery`
 *   take.
 *
 * @example
 * ```ts
 * import { QueryClient } from "@tanstack/react-query";
 * import { Mountaya } from "@mountaya/client";
 * import { itinerariesInfiniteQueryOptions } from "@mountaya/client-react/query";
 *
 * const queryClient = new QueryClient();
 * const mountaya = new Mountaya({ credential: { publishableKey: "pk_live_…" } });
 *
 * await queryClient.prefetchInfiniteQuery(
 *   itinerariesInfiniteQueryOptions(mountaya, { sort: "name", status: "published" }),
 * );
 * ```
 */
export const itinerariesInfiniteQueryOptions = (
  client: Mountaya,
  query?: QueryOf<"getItineraries">,
): StudioInfiniteQueryOptions<BodyOf<Itineraries["getItineraries"]>> =>
  listing(mountayaInfiniteQueryOptions(client, "getItineraries", { query }), (cursor, signal) =>
    client.studio.itineraries.getItineraries({ query: { ...query, cursor } }, { signal }),
  );

/**
 * collectionsInfiniteQueryOptions reads the collections the client's credential reaches, a page
 * at a time, filtered and sorted by `query`, each carrying the caller's role in it.
 *
 * `itinerary_id` and `zone_id` list only the collections holding that itinerary or that zone,
 * which is how a picker shows where something is filed. The key alone, or with a session token
 * naming nobody, lists the organization's public collections; with a session token naming a
 * member, every one, private included. A key lists no personal collection.
 *
 * @param client The client that reads the listing, and whose credential scopes the entry.
 * @param query The listing's filters, its `sort` and its page size. Omitted, every collection
 *   the credential reaches, most recently changed first.
 * @returns The options `useInfiniteQuery`, `prefetchInfiniteQuery` and `fetchInfiniteQuery`
 *   take.
 */
export const collectionsInfiniteQueryOptions = (
  client: Mountaya,
  query?: QueryOf<"getCollections">,
): StudioInfiniteQueryOptions<BodyOf<Collections["getCollections"]>> =>
  listing(mountayaInfiniteQueryOptions(client, "getCollections", { query }), (cursor, signal) =>
    client.studio.collections.getCollections({ query: { ...query, cursor } }, { signal }),
  );

/**
 * zonesInfiniteQueryOptions reads the zones the client's credential reaches, a page at a time,
 * filtered and sorted by `query`.
 *
 * The listing is always the organization of the client's key, and the session decides which
 * zones appear, as it does for itineraries. The key alone, or with a session token naming nobody,
 * lists the public zones that are currently published, each as last published, and none while
 * the organization's agreement does not include Map Publishing; with a session token naming a
 * member, the private and unpublished ones too, each as drafted. A personal zone, owned by no
 * organization, never appears.
 *
 * @param client The client that reads the listing, and whose credential scopes the entry.
 * @param query The listing's filters, its `sort` and its page size. Omitted, every zone the
 *   credential reaches, most recently changed first.
 * @returns The options `useInfiniteQuery`, `prefetchInfiniteQuery` and `fetchInfiniteQuery`
 *   take.
 */
export const zonesInfiniteQueryOptions = (
  client: Mountaya,
  query?: QueryOf<"getZones">,
): StudioInfiniteQueryOptions<BodyOf<Zones["getZones"]>> =>
  listing(mountayaInfiniteQueryOptions(client, "getZones", { query }), (cursor, signal) =>
    client.studio.zones.getZones({ query: { ...query, cursor } }, { signal }),
  );

/** ItineraryReadOptions is what an itinerary's read takes beside its id. */
export interface ItineraryReadOptions {
  /**
   * Which version of the itinerary to read: the draft the editor works on, or the last
   * publication, which is what the public is served.
   *
   * @default "draft"
   */
  readonly surface?: "draft" | "published" | undefined;
}

/**
 * itineraryQueryOptions reads one itinerary whole: its segments, waypoints and geometry.
 *
 * `{ surface: "published" }` reads the last publication instead of the draft, which is what a
 * preview shows so it renders what the public is served. It lands on its own cache entry: the
 * editor holds the draft under the bare key, so folding them together would let a preview
 * overwrite what is being edited.
 *
 * The response carries each segment's analysis arrays exactly as the wire spells them, where
 * `null` means both "not computed" and "not entitled". A studio wants those as empty arrays;
 * apply `@mountaya/elements`' `withItineraryAnalysis` at the read edge to get them. This package
 * holds no map vocabulary, so it cannot normalize them for you.
 *
 * @param client The client that reads the itinerary, and whose credential scopes the entry.
 * @param itineraryId The itinerary to read. While it is `undefined` or empty the read is
 *   disabled.
 * @param options The version to read.
 * @returns The options `useQuery`, `prefetchQuery` and `ensureQueryData` take.
 * @throws MountayaConfigurationError As the fetcher's rejection, before anything is sent, for an
 *   `itineraryId` that is `undefined` or empty.
 *
 * @example
 * ```ts
 * import { QueryClient } from "@tanstack/react-query";
 * import { Mountaya } from "@mountaya/client";
 * import { itineraryQueryOptions } from "@mountaya/client-react/query";
 *
 * declare const itineraryId: string;
 *
 * const queryClient = new QueryClient();
 * const mountaya = new Mountaya({ credential: { publishableKey: "pk_live_…" } });
 *
 * await queryClient.prefetchQuery(itineraryQueryOptions(mountaya, itineraryId));
 * ```
 */
export const itineraryQueryOptions = (
  client: Mountaya,
  itineraryId: string | undefined,
  options?: ItineraryReadOptions,
): StudioQueryOptions<BodyOf<Itineraries["getItinerary"]>> => {
  const surface = options?.surface;

  return {
    ...shared,
    enabled: isNamed(itineraryId) && isScopeSettled(client),
    staleTime: ENTITY_STALE_MS,
    queryFn: async ({ signal }) =>
      client.studio.itineraries.getItinerary(
        {
          itinerary_id: requireId(itineraryId, "itineraryQueryOptions", "itinerary"),
          // Absence is how the draft surface is spelled, so the query object is omitted entirely
          // rather than carrying an explicit "draft".
          query: surface === "published" ? { surface: "published" } : undefined,
        },
        { signal },
      ),
    queryKey: taggedKey(
      studioResourceKey(scopeOf(client), { id: itineraryId ?? "", kind: "itinerary", surface }),
    ),
  };
};

/**
 * itinerarySpecificationQueryOptions reads one itinerary's tile specification: the single vector
 * source and the render-layers that draw THAT itinerary and nothing else.
 *
 * It is the scoped alternative to the aggregate layer manifest, which draws every itinerary the
 * caller can view. A viewer configured for one route registers this instead, so the map shows
 * the route it was pointed at rather than the publisher's whole library. The specification is
 * static per itinerary, so the entry is shared and fetched once.
 *
 * @param client The client that reads the specification, and whose credential scopes the entry.
 * @param itineraryId The itinerary to read. While it is `undefined` or empty the read is
 *   disabled.
 * @returns The options `useQuery`, `prefetchQuery` and `ensureQueryData` take.
 * @throws MountayaConfigurationError As the fetcher's rejection, before anything is sent, for an
 *   `itineraryId` that is `undefined` or empty.
 */
export const itinerarySpecificationQueryOptions = (
  client: Mountaya,
  itineraryId: string | undefined,
): StudioQueryOptions<BodyOf<Tiles["getItinerarySpecification"]>> => ({
  ...shared,
  enabled: isNamed(itineraryId) && isScopeSettled(client),
  staleTime: ENTITY_STALE_MS,
  queryFn: async ({ signal }) =>
    client.tiles.getItinerarySpecification(
      {
        itinerary_id: requireId(itineraryId, "itinerarySpecificationQueryOptions", "itinerary"),
      },
      { signal },
    ),
  queryKey: taggedKey(
    studioResourceKey(scopeOf(client), {
      id: itineraryId ?? "",
      kind: "itinerarySpecification",
    }),
  ),
});

/**
 * collectionQueryOptions reads one collection's own record: its name, description, author,
 * privacy and owning organization.
 *
 * The read for a surface holding nothing but an id: an embed wizard resolving which organization
 * to scope a publishable key to, a share view naming what it is showing.
 *
 * @param client The client that reads the collection, and whose credential scopes the entry.
 * @param collectionId The collection to read. While it is `undefined` or empty the read is
 *   disabled.
 * @returns The options `useQuery`, `prefetchQuery` and `ensureQueryData` take.
 * @throws MountayaConfigurationError As the fetcher's rejection, before anything is sent, for a
 *   `collectionId` that is `undefined` or empty.
 */
export const collectionQueryOptions = (
  client: Mountaya,
  collectionId: string | undefined,
): StudioQueryOptions<BodyOf<Collections["getCollection"]>> => ({
  ...shared,
  enabled: isNamed(collectionId) && isScopeSettled(client),
  staleTime: ENTITY_STALE_MS,
  queryFn: async ({ signal }) =>
    client.studio.collections.getCollection(
      { collection_id: requireId(collectionId, "collectionQueryOptions", "collection") },
      { signal },
    ),
  queryKey: taggedKey(
    studioResourceKey(scopeOf(client), { id: collectionId ?? "", kind: "collection" }),
  ),
});

/**
 * collectionSpecificationQueryOptions reads a collection's tile specification: the vector source
 * and render-layers that draw the collection's whole content (its route, its itineraries and its
 * zones).
 *
 * The collection-scoped twin of {@link itinerarySpecificationQueryOptions}, static in the same
 * way.
 *
 * `query` decides which of the collection's layers the specification declares (`with_zones`,
 * `with_itineraries`); a layer it leaves out is the organization's own default, as the server
 * resolves it for the credential. Each query lands on its own cache entry.
 *
 * @param client The client that reads the specification, and whose credential scopes the entry.
 * @param collectionId The collection to read. While it is `undefined` or empty the read is
 *   disabled.
 * @param query The layers to declare. Omitted, every layer is the organization's default.
 * @returns The options `useQuery`, `prefetchQuery` and `ensureQueryData` take.
 * @throws MountayaConfigurationError As the fetcher's rejection, before anything is sent, for a
 *   `collectionId` that is `undefined` or empty.
 */
export const collectionSpecificationQueryOptions = (
  client: Mountaya,
  collectionId: string | undefined,
  query?: SpecificationQueryOf<"getCollectionSpecification">,
): StudioQueryOptions<BodyOf<Tiles["getCollectionSpecification"]>> => ({
  ...shared,
  enabled: isNamed(collectionId) && isScopeSettled(client),
  staleTime: ENTITY_STALE_MS,
  queryFn: async ({ signal }) =>
    client.tiles.getCollectionSpecification(
      {
        collection_id: requireId(collectionId, "collectionSpecificationQueryOptions", "collection"),
        query,
      },
      { signal },
    ),
  queryKey: taggedKey(
    studioResourceKey(scopeOf(client), {
      id: collectionId ?? "",
      kind: "collectionSpecification",
      query,
    }),
  ),
});

/** ZoneReadOptions is what a zone's read takes beside its id. */
export interface ZoneReadOptions {
  /**
   * Which version of the zone to read: the draft the editor works on, or the last publication,
   * which is what the public is served.
   *
   * @default "draft"
   */
  readonly surface?: "draft" | "published" | undefined;
}

/**
 * zoneQueryOptions reads one zone's canonical detail: its full, unclipped geometry and its
 * metadata.
 *
 * Unclipped is the point. The tiles a map draws carry the zone cut to tile boundaries, which is
 * right for rendering and wrong for editing; an edit flow reads this.
 *
 * `{ surface: "published" }` reads the last publication instead of the draft, which is what the
 * public is served. It lands on its own cache entry, as an itinerary's does, so a read of the
 * publication never overwrites the draft the editor holds.
 *
 * @param client The client that reads the zone, and whose credential scopes the entry.
 * @param zoneId The zone to read. While it is `undefined` or empty the read is disabled.
 * @param options The version to read.
 * @returns The options `useQuery`, `prefetchQuery` and `ensureQueryData` take.
 * @throws MountayaConfigurationError As the fetcher's rejection, before anything is sent, for a
 *   `zoneId` that is `undefined` or empty.
 */
export const zoneQueryOptions = (
  client: Mountaya,
  zoneId: string | undefined,
  options?: ZoneReadOptions,
): StudioQueryOptions<BodyOf<Zones["getZone"]>> => {
  const surface = options?.surface;

  return {
    ...shared,
    enabled: isNamed(zoneId) && isScopeSettled(client),
    staleTime: ENTITY_STALE_MS,
    queryFn: async ({ signal }) =>
      client.studio.zones.getZone(
        {
          // Absence is how the draft surface is spelled, so the query object is omitted entirely
          // rather than carrying an explicit "draft".
          query: surface === "published" ? { surface: "published" } : undefined,
          zone_id: requireId(zoneId, "zoneQueryOptions", "zone"),
        },
        { signal },
      ),
    queryKey: taggedKey(
      studioResourceKey(scopeOf(client), { id: zoneId ?? "", kind: "zone", surface }),
    ),
  };
};
